import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { AppRoute } from '@/common/app/routes';
import { timeStringFormatShortForm } from '@/common/lib/timer';
import { CompleteContractAnimationContext } from '@/components/Animations/CompleteContractAnimation/context';
import { useAppSelector } from '@/redux/store';

const BINGO_ICON = '/img/bingo/bingo-icon.png';
const BINGO_BADGE = '/img/bingo/icon-in-main.png';

const BingoEventBadge: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isHome = useMatch(AppRoute.MAIN);
  const bingoEvent = useAppSelector((state) => state.shop.bingoEvent);
  const { animationStep } = useContext(CompleteContractAnimationContext);

  if (!isHome) return null;

  if (!bingoEvent) return null;

  const isRatingWindow = bingoEvent.phase === 'ratingWindow';
  const seconds = isRatingWindow
    ? (bingoEvent.ratingClosesInSeconds ?? 0)
    : bingoEvent.endsInSeconds;

  if (seconds <= 0) return null;

  const target = isRatingWindow ? AppRoute.BINGO_LEADERBOARD : AppRoute.BINGO;

  return (
    <>
      <AnimatePresence>
        {animationStep === 2 && (
          <motion.img
            layoutId="contract-complete-bingo-balls"
            src={BINGO_ICON}
            alt=""
            className="w-10 h-10 object-contain absolute"
            style={{ zIndex: -2 }}
            transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
            exit={{
              opacity: 0,
              transition: { duration: 0.5, ease: 'easeOut' },
            }}
          />
        )}
      </AnimatePresence>
      <button
        className={cn(
          'h-16 flex flex-col pointer-events-auto items-center',
          className,
        )}
        onClick={() => navigate(target)}
      >
        <div className="w-16 size-12 relative flex items-center justify-center">
          <img
            src={BINGO_BADGE}
            alt="bingo"
            className="absolute w-[125%] mt-2 object-contain"
          />
        </div>
        <div
          className={cn(
            '-mt-2 h-6 rounded-full bg-black/60 backdrop-blur-2xl flex items-center justify-center text-xs font-bold px-2 shadow-glass-blur',
            isRatingWindow ? 'text-[#FFC83A]' : 'text-[#FF4444]',
          )}
        >
          {isRatingWindow
            ? t('Results')
            : timeStringFormatShortForm(seconds * 1000, true)}
        </div>
      </button>
    </>
  );
};

export { BingoEventBadge };
